import { useEffect, useMemo, type FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { joiResolver } from '@hookform/resolvers/joi';
import { Alert } from 'components/atoms/Alert';
import type { APC } from 'api/v2/users/APC';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import ProfessionSelect from 'components/atoms/Form/ProfessionSelect';
import { Select } from 'components/atoms/Form/Select';
import SteuernummerField from 'components/pages/Taxes/Tax/SteuernummerField';
import { OnboardingStepWrapper } from 'components/organisms/OnboardingStepLayout';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useFeatures from 'hooks/useFeatures';
import useSettingsSelectOptions from 'hooks/useSettingsSelectOptions';
import {
  AccountSettingsFieldsNamesEnum,
  MonthlyPaymentVolume,
} from 'types/settings.types';
import { AvailableCountries, type IUser } from 'types/users.types';
import formatMoney from 'utils/formatMoney';
import {
  constructAccountDetailsFormDefaultValues,
  normalizeMaskedInputs,
} from 'utils/settings/settingsHelpers';
import { getSelectOptionValue } from 'utils/helpers';
import type { ActivationStepProps } from '../../ActivationFlow/types';
import {
  BEBusinessActivitySchema,
  DEBusinessActivitySchema,
} from '../../utils/validation';
import { useKBOAddress } from '../hooks/useKBOAddress';
import type { BankingSignupAccountDetails } from '../constants/onboarding.types';

const MONTHS_PER_YEAR = 12;

/** Select labels show yearly volume; values stay {@link MonthlyPaymentVolume} (monthly) for the API. */
function getMonthlyPaymentVolumeYearlyDisplayOptions(
  t: TFunction,
  formatMoneyFn: (amount: number) => string,
) {
  const yearly = (monthlyAmount: number) =>
    formatMoneyFn(monthlyAmount * MONTHS_PER_YEAR);

  return [
    {
      label: t('less_than_amount', {
        amount: yearly(10000),
      }),
      value: MonthlyPaymentVolume.LessThan10000,
    },
    {
      label: t('between_amounts', {
        amount1: yearly(10000),
        amount2: yearly(50000),
      }),
      value: MonthlyPaymentVolume.Between10000And50000,
    },
    {
      label: t('between_amounts', {
        amount1: yearly(50000),
        amount2: yearly(100000),
      }),
      value: MonthlyPaymentVolume.Between50000And100000,
    },
    {
      label: t('more_than_amount', {
        amount: yearly(100000),
      }),
      value: MonthlyPaymentVolume.MoreThan100000,
    },
  ];
}

interface BusinessActivityStepProps extends ActivationStepProps {
  currentAccountDetailsValues?: BankingSignupAccountDetails;
  onConfirm: (values: BankingSignupAccountDetails) => void;
  isConfirming?: boolean;
}

const BusinessActivityStep: FC<BusinessActivityStepProps> = ({
  onConfirm,
  currentAccountDetailsValues,
  isConfirming,
  progressSegments,
  onProgressSegmentClick,
  title,
  subtitle,
  icon,
  showProgressBar,
  showBackButton,
  onBack,
  isUpdatingStep,
}) => {
  const { t } = useTranslation();
  const user = useGetCurrentCustomerData().data as IUser;
  const isBelgium = user?.country === AvailableCountries.belgium;

  const defaultValues = useMemo(
    () =>
      constructAccountDetailsFormDefaultValues(
        currentAccountDetailsValues || user,
      ),
    [currentAccountDetailsValues, user],
  );

  const schema = isBelgium
    ? BEBusinessActivitySchema()
    : DEBusinessActivitySchema();

  const methods = useForm({
    defaultValues,
    resolver: joiResolver(schema, {
      allowUnknown: true,
      abortEarly: false,
    }),
  });

  const setAndValidate: typeof methods.setValue = (name, value, options) =>
    methods.setValue(name, value, { shouldValidate: true, ...options });

  const settingsOptions = useSettingsSelectOptions(
    user?.country as AvailableCountries,
  );

  const [APC1_value, APC2_value, occupation_value] = methods.watch([
    AccountSettingsFieldsNamesEnum.APC1,
    AccountSettingsFieldsNamesEnum.APC2,
    AccountSettingsFieldsNamesEnum.occupation,
  ]);

  return (
    <OnboardingStepWrapper
      showProgressBar={showProgressBar}
      progressSegments={progressSegments}
      onProgressSegmentClick={onProgressSegmentClick}
      title={title}
      subtitle={subtitle}
      icon={icon}
      showBackButton={showBackButton}
      onBack={onBack}
    >
      <FormProvider {...methods}>
        <form
          className="w-[80%] mx-auto"
          onSubmit={methods.handleSubmit(() => onConfirm(methods.getValues()))}
        >
          <FieldConnector
            name={AccountSettingsFieldsNamesEnum.monthlyPaymentVolume}
            className="mt-3"
            label={t(
              'accountable_banking.onboarding.form.estimate_yearly_volume.label',
            )}
            hint={t(
              'accountable_banking.onboarding.form.estimate_yearly_volume.hint',
            )}
          >
            {({ field, name, label }) => (
              <Select
                {...field}
                name={name}
                placeholder={label}
                options={getMonthlyPaymentVolumeYearlyDisplayOptions(
                  t,
                  formatMoney,
                )}
                onChange={({ value }) => field.onChange(value)}
              />
            )}
          </FieldConnector>

          <FieldConnector
            name={[
              AccountSettingsFieldsNamesEnum.APC1,
              AccountSettingsFieldsNamesEnum.APC2,
              AccountSettingsFieldsNamesEnum.occupation,
            ]}
            className="mt-3"
            label={t('accountable_banking.onboarding.form.activity.heading')}
          >
            {({ field: { error, ...field } }) => (
              <ProfessionSelect
                {...field}
                creatable
                error={error}
                controlShouldRenderValue={APC2_value ? true : undefined}
                isClearable
                themeOptions={{
                  hideDropdownIndicator: true,
                }}
                value={
                  occupation_value || { APC1: APC1_value, APC2: APC2_value }
                }
                onChange={(option) => {
                  if (!option) {
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC1, null);
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC2, null);
                    setAndValidate(
                      AccountSettingsFieldsNamesEnum.occupation,
                      null,
                    );
                    return;
                  }
                  if (option.__isCreated) {
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC1, null);
                    setAndValidate(AccountSettingsFieldsNamesEnum.APC2, null);
                    setAndValidate(
                      AccountSettingsFieldsNamesEnum.occupation,
                      option.value,
                    );
                    return;
                  }
                  const data = option as APC;
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.APC1,
                    data[AccountSettingsFieldsNamesEnum.APC1],
                  );
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.APC2,
                    data[AccountSettingsFieldsNamesEnum.APC2],
                  );
                  setAndValidate(
                    AccountSettingsFieldsNamesEnum.occupation,
                    null,
                  );
                }}
              />
            )}
          </FieldConnector>

          {isBelgium ? (
            <BETaxFields methods={methods} />
          ) : (
            <DETaxFields methods={methods} settingsOptions={settingsOptions} />
          )}

          <FieldConnector
            name={AccountSettingsFieldsNamesEnum.companyName}
            className="mt-3"
            label={t('settings.companyDetails.companyName')}
            tooltip={t('signup.company_name.field.explanation')}
          >
            {({ field }) => (
              <FreeTextInput
                {...field}
                placeholder={t(
                  'settings.personalDetails.companyNamePlaceholder',
                )}
              />
            )}
          </FieldConnector>

          <FieldConnector
            name={
              AccountSettingsFieldsNamesEnum.additionalBusinessActivityDescription
            }
            className="mt-3"
            label={t('accountable_banking.onboarding.form.website_url.label')}
            optional
          >
            {({ field }) => <FreeTextInput {...field} placeholder="https://" />}
          </FieldConnector>

          <div className="text-center">
            <Button
              type="submit"
              color="bank"
              className="my-6 mt-12 w-full capitalize"
              loading={isConfirming}
              disabled={isUpdatingStep}
            >
              {t('continue')}
            </Button>
          </div>
        </form>
      </FormProvider>
    </OnboardingStepWrapper>
  );
};

// ===========================
// BELGIUM TAX FIELDS (VAT + KBO)
// ===========================

const BETaxFields: FC<{
  methods: ReturnType<typeof useForm>;
}> = ({ methods }) => {
  const { t } = useTranslation();
  const settingsFeatures = useFeatures('settings');
  const { VATNumberMask } = settingsFeatures;
  const vatNumberPlaceholder = useFeatures('settings.vatNumberPlaceholder');

  const [streetValue, cityValue, zipValue, VATNumberValue] = methods.watch([
    AccountSettingsFieldsNamesEnum.street,
    AccountSettingsFieldsNamesEnum.city,
    AccountSettingsFieldsNamesEnum.zip,
    AccountSettingsFieldsNamesEnum.VATNumber,
  ]);

  const { kboAddressMismatch } = useKBOAddress({
    vatNumber: VATNumberValue,
    streetValue,
    cityValue,
    zipValue,
    setValue: methods.setValue,
    setError: methods.setError,
    clearErrors: methods.clearErrors,
  });

  return (
    <>
      <h3 className="mb-1 mt-3 text-primary font-semibold">
        {t('settings.companyDetails.vatNumber')}
      </h3>

      <FieldConnector
        className="mt-3 flex-1"
        name={AccountSettingsFieldsNamesEnum.VATNumber}
      >
        {({ field, name }) => (
          <FreeTextInput
            {...field}
            placeholder={vatNumberPlaceholder}
            name={name}
            type="maskedInput"
            maskProps={{
              mask: VATNumberMask,
            }}
            onChange={({ target: { value: inputValue } }) =>
              field.onChange(normalizeMaskedInputs(inputValue))
            }
          />
        )}
      </FieldConnector>

      {kboAddressMismatch && (
        <Alert
          className="mt-3"
          type="error"
          description={t('accountable_banking.onboarding.form.be.kbo.content')}
        />
      )}
    </>
  );
};

// ===========================
// GERMANY TAX FIELDS (Steuernummer + Finanzamt)
// ===========================

const DETaxFields: FC<{
  methods: ReturnType<typeof useForm>;
  settingsOptions: ReturnType<typeof useSettingsSelectOptions>;
}> = ({ methods, settingsOptions }) => {
  const { t } = useTranslation();

  const stateValue = methods.watch(AccountSettingsFieldsNamesEnum.state);

  useEffect(() => {
    if (stateValue) {
      methods.setValue(
        AccountSettingsFieldsNamesEnum.taxOfficeState,
        stateValue,
      );
    }
  }, [stateValue]);

  return (
    <>
      <FieldConnector
        className="flex-1 my-3"
        name={AccountSettingsFieldsNamesEnum.steuernummer}
        label={`${t('settings.companyDetails.steuernummer')} (Steuernummer)`}
        forceHideError
      >
        {({ field }) => (
          <SteuernummerField
            {...field}
            stateFieldName={AccountSettingsFieldsNamesEnum.state}
            taxOfficeStateFieldName={
              AccountSettingsFieldsNamesEnum.taxOfficeState
            }
            isBankingOnboarding
          />
        )}
      </FieldConnector>

      <FieldConnector
        className="my-3"
        name={AccountSettingsFieldsNamesEnum.taxOfficeState}
        label={t('accountable_banking.onboarding.form.taxOfficeState')}
        optional
        tooltip={t(
          'accountable_banking.onboarding.form.taxOfficeState_tooltip',
        )}
      >
        {({ name, field }) => (
          <Select
            {...field}
            name={name}
            placeholder={t(
              'accountable_banking.onboarding.form.taxOfficeState_placeholder',
            )}
            options={settingsOptions.germanStatesOptions}
            isDisabled={!stateValue}
            value={
              getSelectOptionValue(
                settingsOptions.germanStatesOptions,
                field.value,
              )?.value
            }
            // todo: consider remove any type
            onChange={({ value }: any) => {
              field.onChange(value);
              // todo: consider remove any type
              methods.clearErrors(name as any);
            }}
          />
        )}
      </FieldConnector>
    </>
  );
};

export default BusinessActivityStep;
